import { User, AuthState } from '../types';
import { LOCAL_STORAGE_KEYS } from '../utils/constants';
import { API_ENDPOINTS } from '../config/api-endpoints';

interface LoginData {
  email: string;
  password: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface LoginResponse {
  status: string;
  data: {
    token: string;
    user: User;
  };
  message?: string;
}

export interface CurrentUserResponse {
  status: string;
  data: {
    user_id: number;
    username: string;
    role: string;
    email: string;
    facebook_linked: boolean;
  };
}

class AuthService {
  private static instance: AuthService;
  private baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Get current user from localStorage
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }

  // Get auth token from localStorage
  getToken(): string | null {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Login user
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    try {
      const response = await fetch(`${this.baseURL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.status || 'Нэвтрэхэд алдаа гарлаа');
      }

      if (data.status === 'successfully logged in') {
        // Store token and user data
        localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, data.data.token);
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(data.data.user));
        return { user: data.data.user, token: data.data.token };
      } else {
        throw new Error('Нэвтрэхэд алдаа гарлаа');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check if the server is running.');
      }
      throw error;
    }
  }

  // Exchange a Facebook access token for the backend JWT
  async loginWithFacebook(accessToken: string): Promise<{ user: User; token: string }> {
    try {
      const response = await fetch(`${this.baseURL}${API_ENDPOINTS.FACEBOOK_LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.status || data.message || 'Facebook нэвтрэхэд алдаа гарлаа');
      }

      localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, data.data.token);
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(data.data.user));
      return { user: data.data.user, token: data.data.token };
    } catch (error) {
      console.error('Facebook login error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check if the server is running.');
      }
      throw error;
    }
  }

  // Link a Facebook account to the currently authenticated user
  async linkFacebook(accessToken: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${this.baseURL}${API_ENDPOINTS.FACEBOOK_LINK}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ access_token: accessToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('Энэ Facebook акаунт өөр хэрэглэгчид холбогдсон байна');
      }
      throw new Error(data.status || data.message || 'Facebook холбоход алдаа гарлаа');
    }
  }

  // Read facebook_linked status and other details for the current user
  async fetchCurrentUser(): Promise<CurrentUserResponse['data']> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${this.baseURL}${API_ENDPOINTS.GET_CURRENT_USER}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const data: CurrentUserResponse = await response.json();

    if (!response.ok) {
      throw new Error('Failed to fetch current user');
    }

    return data.data;
  }

  // Register user
  async register(userData: RegisterData): Promise<{ user: User; token: string }> {
    try {
      // Mock registration - in real app, this would be an API call
      const mockUser: User = {
        id: Date.now().toString(),
        email: userData.email,
        first_name: userData.name.split(' ')[0] || userData.name,
        last_name: userData.name.split(' ')[1] || '',
        username: userData.email.split('@')[0],
        role: 'student',
        created_at: new Date().toISOString(),
        enrolledCourses: [],
      };

      const mockToken = 'mock_jwt_token_' + Date.now();

      // Store in localStorage
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(mockUser));
      localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, mockToken);

      return { user: mockUser, token: mockToken };
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error('Registration failed');
    }
  }

  // Logout user
  logout(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
  }

  // Update user profile
  async updateProfile(updates: Partial<User>): Promise<User> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const updatedUser = { ...currentUser, ...updates };
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw new Error('Failed to update profile');
    }
  }

  // Get authorization headers
  getAuthHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Make authenticated API call
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const authOptions: RequestInit = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    };

    const response = await fetch(url, authOptions);

    // If token is invalid, logout user
    if (response.status === 401) {
      this.logout();
      throw new Error('Authentication failed');
    }

    return response;
  }

  // Get initial auth state
  getInitialAuthState(): AuthState {
    return {
      user: this.getCurrentUser(),
      isAuthenticated: this.isAuthenticated(),
      loading: false,
    };
  }
}

export default AuthService.getInstance();
